class FilterRange extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._drag = {
      active: false,
      handle: null,
      rect: null,
      rafId: null,
    };

    this._state = {
      min: 0,
      max: 100,
      selectedMin: 0,
      selectedMax: 100,
      currency: '€',
    };

    this._refs = {};
    this._onCommit = null;
  }
  getId() {
    return this.closest('.shopify-section')?.id || null;
  }
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.render();
    this.cacheRefs();
    this.attachEvents();
    this.syncUI();
  }

  disconnectedCallback() {
    this.detachGlobalEvents();
  }

  set onCommit(fn) {
    this._onCommit = fn;
  }

  setState(nextState = {}) {
    this._state = {
      ...this._state,
      ...nextState,
    };

    this.normalizeState();
    this.syncUI();
  }

  getState() {
    return { ...this._state };
  }

  normalizeState() {
    const min = Number.isFinite(this._state.min) ? this._state.min : 0;
    const max = Number.isFinite(this._state.max) ? this._state.max : min;

    let selectedMin = Number.isFinite(this._state.selectedMin) ? this._state.selectedMin : min;
    let selectedMax = Number.isFinite(this._state.selectedMax) ? this._state.selectedMax : max;

    selectedMin = this.clamp(selectedMin, min, max);
    selectedMax = this.clamp(selectedMax, selectedMin, max);

    this._state.min = min;
    this._state.max = max;
    this._state.selectedMin = selectedMin;
    this._state.selectedMax = selectedMax;
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  toPercent(value) {
    const { min, max } = this._state;
    if (max <= min) return 0;
    return this.clamp(((value - min) / (max - min)) * 100, 0, 100);
  }

  toValue(percent) {
    const { min, max } = this._state;
    if (percent <= 0) return min;
    if (percent >= 100) return max;
    return Math.round(min + ((max - min) * percent) / 100);
  }

  render() {
    this.innerHTML = `
      <div class="filter-price-inputs" role="group" aria-label="Price range filter" aria-live="polite">
        <div class="price-input filter-price-inputs__min">
          <input
            type="text"
            name="min-price"
            class="price-input__input input"
            aria-label="Minimum price"
            inputmode="numeric"
          />
        </div>

        <div class="filter-price-inputs__seperator"></div>

        <div class="price-input filter-price-inputs__max">
          <input
            type="text"
            name="max-price"
            class="price-input__input input"
            aria-label="Maximum price"
            inputmode="numeric"
          />
        </div>
      </div>

      <div
        class="range-slider"
        role="group"
        aria-label="Price range filter"
        aria-description="Use arrow keys to adjust the minimum and maximum price values"
      >
        <div class="range-slider__container">
          <div class="range-slider__val-left" style="width: 0%;"></div>
          <div class="range-slider__val-right" style="width: 0%;"></div>
          <div class="range-slider__val-range" style="left: 0%; right: 0%;"></div>
          <span class="range-slider__handle range-slider__handle-left" style="left: 0%;"></span>
          <span class="range-slider__handle range-slider__handle-right" style="left: 100%;"></span>
        </div>

        <input
          class="range-slider__input-left field__input"
          type="range"
          min="0"
          max="100"
          step="1"
          value="0"
          aria-label="Minimum price"
          tabindex="0"
          id="${this.getId() ? this.getId() + '-min-range' : 'min-range'}"
        />

        <input
          class="range-slider__input-right field__input"
          type="range"
          min="0"
          max="100"
          step="1"
          value="100"
          aria-label="Maximum price"
          tabindex="0"
          id="${this.getId() ? this.getId() + '-max-range' : 'max-range'}"
        />
      </div>
    `;
  }

  cacheRefs() {
    this._refs.minWrapper = this.querySelector('.filter-price-inputs__min');
    this._refs.maxWrapper = this.querySelector('.filter-price-inputs__max');
    this._refs.minTextInput = this.querySelector('.filter-price-inputs__min input');
    this._refs.maxTextInput = this.querySelector('.filter-price-inputs__max input');

    this._refs.sliderContainer = this.querySelector('.range-slider__container');
    this._refs.valLeft = this.querySelector('.range-slider__val-left');
    this._refs.valRight = this.querySelector('.range-slider__val-right');
    this._refs.valRange = this.querySelector('.range-slider__val-range');
    this._refs.handleLeft = this.querySelector('.range-slider__handle-left');
    this._refs.handleRight = this.querySelector('.range-slider__handle-right');
    this._refs.inputLeft = this.querySelector('.range-slider__input-left');
    this._refs.inputRight = this.querySelector('.range-slider__input-right');
  }

  attachEvents() {
    const { minTextInput, maxTextInput, inputLeft, inputRight, handleLeft, handleRight } =
      this._refs;

    inputLeft.addEventListener('input', this.handleLeftRangeInput);
    inputRight.addEventListener('input', this.handleRightRangeInput);

    inputLeft.addEventListener('change', this.handleRangeCommit);
    inputRight.addEventListener('change', this.handleRangeCommit);

    minTextInput.addEventListener('change', this.handleMinTextChange);
    maxTextInput.addEventListener('change', this.handleMaxTextChange);

    handleLeft.addEventListener('mousedown', (e) => this.startDrag(e, 'left'));
    handleRight.addEventListener('mousedown', (e) => this.startDrag(e, 'right'));

    handleLeft.addEventListener('touchstart', (e) => this.startDrag(e, 'left'), { passive: false });
    handleRight.addEventListener('touchstart', (e) => this.startDrag(e, 'right'), {
      passive: false,
    });
  }

  detachGlobalEvents() {
    document.removeEventListener('mousemove', this.handleDocumentMouseMove);
    document.removeEventListener('mouseup', this.stopDrag);
    document.removeEventListener('touchmove', this.handleDocumentTouchMove);
    document.removeEventListener('touchend', this.stopDrag);
    document.removeEventListener('touchcancel', this.stopDrag);
  }

  handleLeftRangeInput = (e) => {
    const rightPercent = Number(this._refs.inputRight.value);
    const leftPercent = this.clamp(Number(e.currentTarget.value), 0, rightPercent);

    this._refs.inputLeft.value = String(leftPercent);
    this._state.selectedMin = this.toValue(leftPercent);
    this.syncUI();
  };

  handleRightRangeInput = (e) => {
    const leftPercent = Number(this._refs.inputLeft.value);
    const rightPercent = this.clamp(Number(e.currentTarget.value), leftPercent, 100);

    this._refs.inputRight.value = String(rightPercent);
    this._state.selectedMax = this.toValue(rightPercent);
    this.syncUI();
  };

  handleRangeCommit = () => {
    this.commit();
  };

  handleMinTextChange = (e) => {
    const nextMin = Number(e.currentTarget.value);
    const currentMax = this._state.selectedMax;

    if (Number.isNaN(nextMin) || nextMin > currentMax) {
      this.syncUI();
      return;
    }

    this._state.selectedMin = this.clamp(nextMin, this._state.min, this._state.max);
    this.syncUI();
    this.commit();
  };

  handleMaxTextChange = (e) => {
    const nextMax = Number(e.currentTarget.value);
    const currentMin = this._state.selectedMin;

    if (Number.isNaN(nextMax) || nextMax < currentMin) {
      this.syncUI();
      return;
    }

    this._state.selectedMax = this.clamp(nextMax, this._state.min, this._state.max);
    this.syncUI();
    this.commit();
  };

  startDrag(event, handle) {
    event.preventDefault();

    this._drag.active = true;
    this._drag.handle = handle;
    this._drag.rect = this._refs.sliderContainer.getBoundingClientRect();

    document.body.style.cursor = 'grabbing';

    document.addEventListener('mousemove', this.handleDocumentMouseMove);
    document.addEventListener('mouseup', this.stopDrag);
    document.addEventListener('touchmove', this.handleDocumentTouchMove, { passive: true });
    document.addEventListener('touchend', this.stopDrag);
    document.addEventListener('touchcancel', this.stopDrag);
  }

  handleDocumentMouseMove = (e) => {
    this.onDragMove(e.clientX);
  };

  handleDocumentTouchMove = (e) => {
    if (!e.touches[0]) return;
    this.onDragMove(e.touches[0].clientX);
  };

  onDragMove(clientX) {
    if (!this._drag.active || !this._drag.rect) return;

    if (this._drag.rafId) {
      cancelAnimationFrame(this._drag.rafId);
    }

    this._drag.rafId = requestAnimationFrame(() => {
      const rect = this._drag.rect;
      const rawPercent = ((clientX - rect.left) / rect.width) * 100;
      const percent = this.clamp(rawPercent, 0, 100);

      if (this._drag.handle === 'left') {
        const rightPercent = this.toPercent(this._state.selectedMax);
        const nextLeft = Math.min(percent, rightPercent);
        this._state.selectedMin = this.toValue(nextLeft);
      } else {
        const leftPercent = this.toPercent(this._state.selectedMin);
        const nextRight = Math.max(percent, leftPercent);
        this._state.selectedMax = this.toValue(nextRight);
      }

      this.syncUI();
      this._drag.rafId = null;
    });
  }

  stopDrag = () => {
    if (!this._drag.active) return;

    this._drag.active = false;
    this._drag.handle = null;
    this._drag.rect = null;

    if (this._drag.rafId) {
      cancelAnimationFrame(this._drag.rafId);
      this._drag.rafId = null;
    }

    document.body.style.cursor = '';
    this.detachGlobalEvents();
    this.commit();
  };

  syncUI() {
    if (!this._initialized) return;

    this.normalizeState();

    const { min, max, selectedMin, selectedMax, currency } = this._state;
    const {
      minWrapper,
      maxWrapper,
      minTextInput,
      maxTextInput,
      inputLeft,
      inputRight,
      valLeft,
      valRight,
      valRange,
      handleLeft,
      handleRight,
    } = this._refs;

    if (minWrapper) minWrapper.style.setProperty('--currency', `'${currency}'`);
    if (maxWrapper) maxWrapper.style.setProperty('--currency', `'${currency}'`);

    if (minTextInput && document.activeElement !== minTextInput) {
      minTextInput.value = String(selectedMin);
      minTextInput.placeholder = String(min);
    }

    if (maxTextInput && document.activeElement !== maxTextInput) {
      maxTextInput.value = String(selectedMax);
      maxTextInput.placeholder = String(max);
    }

    let leftPercent, rightPercent;
    if (min === max) {
      leftPercent = 0;
      rightPercent = 100;
    } else {
      leftPercent = this.toPercent(selectedMin);
      rightPercent = this.toPercent(selectedMax);
    }

    if (inputLeft) inputLeft.value = String(Math.round(leftPercent));
    if (inputRight) inputRight.value = String(Math.round(rightPercent));

    if (valLeft) valLeft.style.width = `${leftPercent}%`;
    if (valRight) valRight.style.width = `${100 - rightPercent}%`;
    if (valRange) {
      valRange.style.left = `${leftPercent}%`;
      valRange.style.right = `${100 - rightPercent}%`;
    }
    if (handleLeft) handleLeft.style.left = `${leftPercent}%`;
    if (handleRight) handleRight.style.left = `${rightPercent}%`;
  }

  commit() {
    if (typeof this._onCommit === 'function') {
      this._onCommit({
        min: this._state.selectedMin,
        max: this._state.selectedMax,
      });
    }

    this.dispatchEvent(
      new CustomEvent('filter-range:commit', {
        detail: {
          min: this._state.selectedMin,
          max: this._state.selectedMax,
        },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('filter-range')) {
  customElements.define('filter-range', FilterRange);
}
